import React from 'react';
import Table from '../Table';

const META = [
	['Prepared by', 'Mereos Engineering Team'],
	['Application', 'dashboard-app (Mereos)'],
	['Integration type', 'Microsoft Entra ID — Multi-Tenant OIDC (Admin Consent)'],
	['Document type', 'Technical Setup & Reference Guide'],
];

const STEPS = [
	{
		title: '1. Application registered in the Mereos Entra tenant',
		body: "An application named dashboard-app was registered under the Mereos Microsoft Entra tenant (mereos11.onmicrosoft.com), configured as a multi-tenant application (\"Accounts in any organizational directory\").",
		reference: {
			label: 'Microsoft Learn — Enable multi-tenant sign-in for an application',
			url: 'https://learn.microsoft.com/en-us/entra/identity-platform/howto-convert-app-to-be-multi-tenant',
		},
	},
	{
		title: '2. Admin consent URL shared with your organization',
		body: "Mereos shares the following admin consent link with your organization's IT administrator:",
		code: 'https://login.microsoftonline.com/common/adminconsent?client_id=754bcf92-ae3b-4f18-84ea-ab67be8c2063',
		bodyAfter: "The common endpoint allows your administrator to sign in with their existing Microsoft org account — Microsoft automatically detects their tenant and scopes the consent to your organization's directory.",
		reference: {
			label: 'Microsoft Learn — Grant tenant-wide admin consent to an application',
			url: 'https://learn.microsoft.com/en-us/entra/identity/enterprise-apps/grant-admin-consent',
		},
	},
	{
		title: "3. dashboard-app appears in your Enterprise Applications",
		body: "Once the admin opens the link and signs in, Microsoft automatically creates a service principal for dashboard-app inside your tenant. It becomes visible under Entra Admin Center → Enterprise Applications on your side.",
		reference: {
			label: 'Microsoft Learn — Permissions and consent in the Microsoft identity platform',
			url: 'https://learn.microsoft.com/en-us/entra/identity-platform/permissions-consent-overview',
		},
	},
	{
		title: '4. Your admin reviews and grants permissions',
		body: "From the Enterprise Applications entry, your administrator opens Permissions and grants admin consent for the requested scopes (openid, profile). This step is your organization explicitly confirming they trust the application with the listed permissions — nothing is granted silently.",
		reference: {
			label: 'Microsoft Learn — Microsoft identity platform admin consent protocols',
			url: 'https://learn.microsoft.com/en-us/entra/identity-platform/v2-admin-consent',
		},
	},
	{
		title: "5. A test user is created in your tenant",
		body: 'A user account is created inside your Entra directory to validate the end-to-end login flow before rolling out to all staff.',
	},
	{
		title: "6. App role assigned to the user in your tenant",
		body: "Your administrator assigns the user to an app role (e.g. Candidate) defined in dashboard-app. The role value is controlled by Mereos and comes back automatically in the roles claim of the ID token on every login — no Microsoft Graph call is needed.",
		reference: {
			label: 'Microsoft Learn — Add app roles to your application and receive them in the token',
			url: 'https://learn.microsoft.com/en-us/entra/identity-platform/howto-add-app-roles-in-apps',
		},
	},
	{
		title: '7. Login validated on the Mereos platform',
		body: 'The test user signs in through the Mereos login screen using "Sign in with Microsoft." Since admin consent was already granted in Step 4, the user is authenticated directly without an individual consent prompt, and the multi-tenant flow completes successfully.',
	},
];

const COMPARISON_COLUMNS = [
	{ heading: 'Aspect', accessor: 'aspect' },
	{ heading: 'Option A — you register your own app', accessor: 'optionA' },
	{
		heading: "Option B — Admin consent to Mereos's app (used)",
		accessor: 'optionB',
		Cell: ({ value }) => <strong className='text-gray-800'>{value}</strong>,
	},
];

const COMPARISON_DATA = [
	{
		aspect: 'Setup effort for your organization',
		optionA: 'Your IT team must register a new app, configure redirect URIs, set API permissions, and manage its own client ID/secret.',
		optionB: 'One admin click on the consent link. Total setup: under 5 minutes.',
	},
	{
		aspect: 'Ongoing maintenance',
		optionA: 'You own the app registration permanently — any update to scopes or redirect URIs must be manually replicated by your IT team for every release.',
		optionB: 'Maintained entirely by Mereos. Updates, scope changes, and bug fixes roll out automatically to all customer tenants.',
	},
	{
		aspect: 'Credential management',
		optionA: 'You would generate and store your own client secret, which becomes a security liability you are responsible for rotating and protecting.',
		optionB: "No client secret is shared with or stored by you. Mereos's existing client ID is public and safe to share.",
	},
	{
		aspect: 'Consistency across customers',
		optionA: "Each customer's registration is configured independently, increasing the chance of misconfiguration and inconsistent behavior between customers.",
		optionB: 'Standard pattern — same app, same flow, used identically across every customer Mereos onboards. Predictable and well-tested.',
	},
	{
		aspect: 'Time to go live',
		optionA: "Requires coordination between Mereos and your IT team to align app settings before anything works.",
		optionB: 'Immediate — works as soon as consent is granted.',
	},
	{
		aspect: 'Microsoft-recommended for SaaS providers',
		optionA: 'Documented for single-organization internal tools, not for external SaaS vendors serving many customers.',
		optionB: 'Yes — this is the documented pattern for multi-tenant SaaS apps. See Microsoft Learn.',
	},
	{
		aspect: 'Permission visibility',
		optionA: 'Same visibility, but the burden of correctly configuring the permission list falls on your IT team rather than Mereos.',
		optionB: 'You see and explicitly approve the exact scopes requested before anything is granted (Step 4).',
	},
];

const PERMISSIONS_COLUMNS = [
	{ heading: 'Scope', accessor: 'scope', Cell: ({ value }) => <code className='text-[13px] bg-gray-100 px-1.5 py-0.5 rounded'>{value}</code> },
	{ heading: 'Purpose', accessor: 'purpose' },
];

const PERMISSIONS_DATA = [
	{
		scope: 'openid',
		purpose: "Required baseline scope to sign the user in and verify their identity.",
	},
	{
		scope: 'profile',
		purpose: "Reads the user's basic profile information (name, display name). Also carries the roles claim populated from app role assignments.",
	},
];

const REFERENCES = [
	{
		label: 'Grant tenant-wide admin consent to an application',
		url: 'https://learn.microsoft.com/en-us/entra/identity/enterprise-apps/grant-admin-consent',
	},
	{
		label: 'Microsoft identity platform admin consent protocols',
		url: 'https://learn.microsoft.com/en-us/entra/identity-platform/v2-admin-consent',
	},
	{
		label: 'Overview of permissions and consent in the Microsoft identity platform',
		url: 'https://learn.microsoft.com/en-us/entra/identity-platform/permissions-consent-overview',
	},
	{
		label: 'Enable multi-tenant sign-in for an application',
		url: 'https://learn.microsoft.com/en-us/entra/identity-platform/howto-convert-app-to-be-multi-tenant',
	},
	{
		label: 'Add app roles to your application and receive them in the token',
		url: 'https://learn.microsoft.com/en-us/entra/identity-platform/howto-add-app-roles-in-apps',
	},
	{
		label: 'Tenant configurations in Microsoft Entra External ID (Workforce vs. External)',
		url: 'https://learn.microsoft.com/en-us/entra/external-id/tenant-configurations',
	},
];

const CALLOUT_PALETTES = {
	blue: 'from-blue-50 to-indigo-50 border-blue-500 text-blue-700',
	green: 'from-green-50 to-emerald-50 border-green-500 text-green-700',
	purple: 'from-purple-50 to-indigo-50 border-purple-400 text-purple-700',
};

const Callout = ({ title, children, color = 'blue' }) => {
	const [from, to, border, text] = CALLOUT_PALETTES[color].split(' ');
	return (
		<div className={`p-6 rounded-lg w-full mb-6 mt-3 bg-gradient-to-r ${from} ${to} border-l-4 ${border} shadow-md`}>
			<p className={`text-xs font-bold uppercase tracking-wide mb-2 ${text}`}>{title}</p>
			<div className='text-gray-600'>{children}</div>
		</div>
	);
};

const MicrosoftSsoIntegrationGuide = () => {
	return (
		<div>
			<div className='text-xs font-semibold tracking-wide text-blue-600 uppercase mb-3'>
				Mereos · Identity &amp; Access Integration
			</div>
			<div className='w-16 h-1 bg-blue-600 mb-6 rounded' />

			<h1 className='text-3xl md:text-4xl font-bold text-gray-900 mb-3'>
				Microsoft Entra ID SSO Integration Guide
			</h1>
			<p className='text-gray-600 mb-8 max-w-3xl'>
				Multi-Tenant Admin Consent Flow — Setup Steps, Microsoft Documentation References,
				and Comparison of Integration Options
			</p>

			<div className='grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 border-t border-b border-gray-200 py-6 mb-12 text-sm'>
				{META.map(([label, value]) => (
					<div key={label} className='flex justify-between sm:justify-start sm:gap-2'>
						<span className='text-gray-500 uppercase tracking-wide text-xs font-semibold sm:min-w-[160px]'>
							{label}
						</span>
						<span className='text-gray-900 font-medium'>{value}</span>
					</div>
				))}
			</div>

			<section className='mb-12'>
				<h2 className='text-2xl font-bold text-gray-800 mb-4'>1. Overview</h2>
				<p className='text-gray-600 mb-4'>
					This document outlines the steps required to connect your organization's Microsoft
					Entra ID (Azure AD) tenant to the Mereos platform using Microsoft's standard{' '}
					<strong>multi-tenant admin consent flow</strong>. This is the same integration
					pattern used by enterprise SaaS platforms such as Zoom, Slack, and Salesforce
					when connecting to a customer's Microsoft 365 / Entra ID environment.
				</p>
				<p className='text-gray-600 mb-4'>
					Mereos has already registered a single application (<code className='text-[13px] bg-gray-100 px-1.5 py-0.5 rounded'>dashboard-app</code>)
					under its own Microsoft Entra tenant. Rather than your organization creating and
					maintaining a separate app registration, your IT administrator grants{' '}
					<strong>admin consent</strong> to Mereos's existing application. This is the
					approach Microsoft documents and recommends for SaaS providers serving multiple
					customer organizations.
				</p>

				<Callout title='Why this matters'>
					Admin consent means your admin approves the integration once, on behalf of the
					whole organization. Individual users do not need to approve permissions
					themselves, and Mereos does not need access to your tenant configuration beyond
					what is explicitly consented to.
				</Callout>
			</section>

			<section className='mb-12'>
				<h2 className='text-2xl font-bold text-gray-800 mb-6'>2. Integration Steps Followed</h2>
				<div className='space-y-6'>
					{STEPS.map((step, index) => (
						<div key={step.title} className='flex gap-4'>
							<div className='flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold text-sm'>
								{index + 1}
							</div>
							<div className='flex-1'>
								<h3 className='text-lg font-semibold text-gray-800 mb-1'>
									{step.title.replace(/^\d+\.\s*/, '')}
								</h3>
								<p className='text-gray-600 mb-1'>{step.body}</p>
								{step.code && (
									<div className='overflow-x-auto bg-[#2D3134] text-gray-100 text-xs rounded p-3 my-2'>
										<code>{step.code}</code>
									</div>
								)}
								{step.bodyAfter && <p className='text-gray-600 mb-1'>{step.bodyAfter}</p>}
								{step.reference && (
									<p className='text-sm text-gray-500 mt-1'>
										Reference:{' '}
										<a
											href={step.reference.url}
											target='_blank'
											rel='noreferrer'
											className='text-blue-600 hover:underline'
										>
											{step.reference.label}
										</a>
									</p>
								)}
							</div>
						</div>
					))}
				</div>

				<Callout title='Result' color='green'>
					Once Steps 1–7 are complete, every user in your tenant can log into Mereos with
					their existing Microsoft work account — no additional per-user setup is required
					on your side.
				</Callout>
			</section>

			<section className='mb-12'>
				<h2 className='text-2xl font-bold text-gray-800 mb-4'>
					3. Comparison: Two Ways to Connect Your Organization to Mereos
				</h2>
				<p className='text-gray-600 mb-4'>
					There are two standard patterns Microsoft documents for connecting an external
					organization's users to a SaaS application. The table below compares the
					alternative of registering and maintaining your own copy of the application
					(Option A) against the approach we recommend (Option B).
				</p>
				<div className='overflow-x-auto'>
					<Table
						column={COMPARISON_COLUMNS}
						data={COMPARISON_DATA}
						showPagination={false}
						onSortedList={() => null}
					/>
				</div>

				<Callout title='Why Option B is recommended' color='green'>
					Option B is the standard integration model Microsoft documents for SaaS
					providers (referred to in Microsoft's documentation as the "admin consent"
					flow). It minimizes setup work and ongoing maintenance for your IT team, avoids
					you having to manage a Microsoft application or secret, and is the same proven
					flow already used successfully across Mereos's other customer tenants.
				</Callout>
			</section>

			<section className='mb-12'>
				<h2 className='text-2xl font-bold text-gray-800 mb-4'>
					4. Permissions Requested by dashboard-app
				</h2>
				<div className='overflow-x-auto'>
					<Table
						column={PERMISSIONS_COLUMNS}
						data={PERMISSIONS_DATA}
						showPagination={false}
						onSortedList={() => null}
					/>
				</div>
				<p className='text-sm text-gray-500 mt-3'>
					No further permissions are requested. Role information is delivered via the ID
					token's <code className='text-[13px] bg-gray-100 px-1.5 py-0.5 rounded'>roles</code>{' '}
					claim using Microsoft's App Roles feature — no Microsoft Graph calls, no
					directory read permissions required.
				</p>
			</section>

			<section className='mb-6'>
				<h2 className='text-2xl font-bold text-gray-800 mb-4'>
					5. Microsoft Documentation References
				</h2>
				<p className='text-gray-600 mb-4'>
					All steps in this guide follow publicly documented Microsoft Entra ID / identity
					platform processes. For your IT or security team to independently verify any
					step above, the following official Microsoft Learn pages are provided:
				</p>
				<ul className='list-disc pl-5 space-y-2 mb-6'>
					{REFERENCES.map((ref) => (
						<li key={ref.url}>
							<a
								href={ref.url}
								target='_blank'
								rel='noreferrer'
								className='text-blue-600 hover:underline'
							>
								{ref.label}
							</a>
						</li>
					))}
				</ul>

				<Callout title='Note' color='purple'>
					This document describes a process that follows Microsoft's published guidance,
					documented on the Microsoft Learn pages above. The steps are not custom or
					specific to Mereos's infrastructure — they follow Microsoft's standard pattern
					for connecting a multi-tenant SaaS application to a customer's Entra ID tenant.
				</Callout>
			</section>
		</div>
	);
};

export default MicrosoftSsoIntegrationGuide;
